import type { Broker } from '@/services/brokers/types';

/**
 * Mesma regra da Api (Portal.Api parties/brokers/domain/party-status.ts): o
 * InsureMO devolve PartyStatus como código — '1' é Valid, '2' é a party
 * desativada no Party Management. Qualquer valor fora da lista (inclusive
 * vazio) é inativo; a Api recusa criar usuário e ativar produto nesses casos.
 */
const ACTIVE_STATUS_VALUES = new Set(['1', 'active', 'valid']);

export function isActiveBrokerStatus(status: unknown): boolean {
  if (typeof status !== 'string' && typeof status !== 'number') return false;
  return ACTIVE_STATUS_VALUES.has(String(status).trim().toLowerCase());
}

export function isActiveBroker(broker: Pick<Broker, 'status'> | undefined) {
  return isActiveBrokerStatus(broker?.status);
}

export const BROKER_CHECK_FAILED_REASON =
  'Não foi possível verificar a corretora. Tente novamente.';

/**
 * Motivo exibido quando a ação está travada. Durante o carregamento não há
 * aviso (o botão só fica desabilitado até a resposta chegar).
 */
export function brokerActionBlockedReason(
  details: { isLoading: boolean; isError: boolean; isActive: boolean },
  action: string
): string | undefined {
  if (details.isLoading || details.isActive) return undefined;
  if (details.isError) return BROKER_CHECK_FAILED_REASON;
  return `Corretora inativa. Não é possível ${action}.`;
}

/** Aviso único da tela, independente de quantas ações ela trava. */
export const BROKER_INACTIVE_NOTICE =
  'Corretora inativa. Não é possível criar, editar, enviar ou baixar documentos.';

/**
 * Junta os guards de uma tela num único aviso. Os botões continuam com o
 * motivo específico no `title`; o aviso da tela é um só. Sem motivo (livre ou
 * carregando) não há aviso; falha de consulta tem o texto próprio.
 */
export function resolveBrokerInactiveNotice(
  guards: ReadonlyArray<BrokerWriteGuard | null | undefined>
): string | undefined {
  const reasons = guards.map(guard => guard?.reason).filter(Boolean);
  if (reasons.length === 0) return undefined;
  if (reasons.includes(BROKER_CHECK_FAILED_REASON)) {
    return BROKER_CHECK_FAILED_REASON;
  }
  return BROKER_INACTIVE_NOTICE;
}

export type BrokerWriteResource = 'quote' | 'proposal' | 'policy';

const BROKER_WRITE_ACTION: Record<BrokerWriteResource, string> = {
  quote: 'criar ou editar cotações',
  proposal: 'criar ou editar propostas',
  // Apólice não tem criar/editar pelo Portal; na prática só se usa `other`.
  policy: 'alterar apólices',
};

/**
 * `write` = criar/editar o recurso; `other` = demais ações sobre ele (e-mail,
 * transmitir, assinatura, subscrição...), com o aviso genérico da Api.
 */
export type BrokerGuardAction = 'write' | 'other';

const BROKER_OTHER_ACTION = 'realizar esta ação';

/**
 * Motivo genérico de ação travada por corretora inativa — o mesmo que o guard
 * `action='other'` produz. Serve para telas que conhecem o status da corretora
 * por linha (ex.: Extrato de Parcelas), sem guard de contexto.
 */
export const BROKER_INACTIVE_ACTION_REASON = `Corretora inativa. Não é possível ${BROKER_OTHER_ACTION}.`;

export type BrokerWriteGuard = { blocked: boolean; reason?: string };

/**
 * Trava de escrita (cotação/proposta) pela corretora do contexto. A Api barra
 * com 403 do mesmo jeito; aqui só evitamos o clique.
 *
 * Sem corretora selecionada ("Todos Corretores") não há o que checar: cada
 * linha é de uma corretora e a Api decide. Com corretora, só libera depois de
 * confirmar que está ativa — enquanto carrega fica travado sem aviso.
 */
export function resolveBrokerWriteGuard(input: {
  hasSelectedBroker: boolean;
  details: { isLoading: boolean; isError: boolean; isActive: boolean };
  resource: BrokerWriteResource;
  action?: BrokerGuardAction;
}): BrokerWriteGuard {
  const { hasSelectedBroker, details, resource, action = 'write' } = input;
  if (!hasSelectedBroker || details.isActive) return { blocked: false };

  const label =
    action === 'write' ? BROKER_WRITE_ACTION[resource] : BROKER_OTHER_ACTION;
  return { blocked: true, reason: brokerActionBlockedReason(details, label) };
}

/**
 * A listagem busca por texto (fuzzy): pesquisar um PartyCode pode trazer outras
 * corretoras. Casa o código exato; o primeiro item só vale quando é o único.
 */
export function findBrokerByCode(
  brokers: Broker[] | undefined,
  code: string
): Broker | undefined {
  const list = brokers ?? [];
  const target = code.trim().toLowerCase();

  return (
    list.find(broker => broker.code?.trim().toLowerCase() === target) ??
    (list.length === 1 ? list[0] : undefined)
  );
}
